/**
 * One Security Center page (/security/:slug): the docs layout the Help
 * Center uses (page list, the page with Silas's byline, "On this page"),
 * then the other Security pages and the ways to reach us.
 */
import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import { SECURITY_CENTER, SECURITY_BASE_PATH } from '@/content/security';
import { securityPageMeta } from '@/content/security/seo';
import HelpShell from '@/components/help/HelpShell';
import HelpSeo from '@/components/help/HelpSeo';
import AuthorByline from '@/components/help/AuthorByline';
import ArticleBody from '@/components/help/ArticleBody';
import OnThisPage from '@/components/help/OnThisPage';
import SecuritySidebar from '@/components/security/SecuritySidebar';
import SecurityIcon from '@/components/security/SecurityIcon';
import SecurityReportPanel from '@/components/security/SecurityReportPanel';
import SecurityNotFound from './SecurityNotFound';

const readingMinutes = (text) => Math.max(1, Math.round(String(text || '').split(/\s+/).filter(Boolean).length / 220));

export default function SecurityPage() {
  const { slug } = useParams();
  const page = SECURITY_CENTER.getPage(slug);
  if (!page) return <SecurityNotFound />;
  const others = SECURITY_CENTER.pages.filter((p) => p.slug !== page.slug);

  return (
    <HelpShell showSearch={false} homeLabel="Security" homeHref={SECURITY_BASE_PATH}>
      <HelpSeo meta={securityPageMeta(page)} />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-10 pb-24">
        <div className="grid gap-12 lg:grid-cols-[250px_minmax(0,1fr)] xl:grid-cols-[250px_minmax(0,1fr)_200px]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pr-2">
              <SecuritySidebar slug={page.slug} />
            </div>
          </aside>

          <article className="min-w-0 max-w-3xl">
            <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-[13px] text-white/50">
              <Link to={SECURITY_BASE_PATH} className="hover:text-white">Security</Link>
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              <span className="text-white/70">{page.navTitle}</span>
            </nav>
            <h1 className="mt-5 text-[30px] sm:text-[38px] leading-[1.15] font-semibold tracking-[-0.02em] text-white">{page.title}</h1>
            <div className="mt-6 pb-7 border-b border-white/[0.08]">
              <AuthorByline author={page.authorProfile} updated={page.updated} minutes={readingMinutes(page.plainText)} />
            </div>

            <div className="mt-4">
              <ArticleBody blocks={page.blocks} />
            </div>

            <section aria-labelledby="more-security" className="mt-14">
              <h2 id="more-security" className="text-[18px] font-medium text-white">More from Security</h2>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                {others.map((p) => (
                  <li key={p.slug}>
                    <Link to={p.path} className="group flex h-full items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5 hover:border-white/20 hover:bg-white/[0.05] transition-colors">
                      <SecurityIcon name={p.icon} className="h-[18px] w-[18px] flex-shrink-0 text-[#C9D0EA]" />
                      <span className="min-w-0 flex-1 text-[15px] font-medium text-white">{p.title}</span>
                      <ArrowUpRight className="h-4 w-4 flex-shrink-0 text-white/30 group-hover:text-white/70" aria-hidden />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            <div className="mt-12">
              <SecurityReportPanel />
            </div>
          </article>

          <aside className="hidden xl:block">
            <div className="sticky top-24">
              <OnThisPage headings={page.headings} />
            </div>
          </aside>
        </div>
      </div>
    </HelpShell>
  );
}
